/**
 * Case Studies data for San Gabriel Solutions
 * Showcases client success stories with measurable outcomes
 */

export const caseStudies = [
  {
    id: 'professional-services-digital-transformation',
    title: 'Transforming Digital Presence for Professional Services Firm',
    client: 'Confidential Professional Services Firm',
    industry: 'professional-services',
    services: ['brand-development-positioning', 'digital-presence-growth'],
    challenge: 'A mid-sized professional services firm struggled with an outdated brand identity and limited online visibility. Their website generated minimal leads, and they lacked a cohesive digital strategy to compete with larger firms in their market. The firm needed to establish thought leadership and attract higher-value clients.',
    strategy: 'We developed a comprehensive rebrand and digital transformation strategy focused on positioning the firm as industry experts. This included brand identity refresh, messaging framework development, and a content-driven SEO strategy to build authority and visibility.',
    execution: 'Implemented new brand identity across all touchpoints, rebuilt website with modern design and user experience, launched thought leadership content program with regular insights and articles, optimized for search engines, and integrated lead capture mechanisms throughout the digital experience.',
    outcomes: [
      { metric: 'Website Traffic', result: '+250%', timeframe: '6 months' },
      { metric: 'Qualified Lead Generation', result: '+180%', timeframe: '6 months' },
      { metric: 'Brand Recognition', result: '+65%', timeframe: '12 months' },
      { metric: 'Average Client Value', result: '+40%', timeframe: '12 months' }
    ],
    testimonial: {
      quote: 'San Gabriel Solutions transformed our market presence. Their strategic approach helped us compete with much larger firms, and the results speak for themselves.',
      author: 'Managing Partner',
      company: 'Professional Services Firm'
    },
    featured: true
  },
  {
    id: 'real-estate-integrated-campaign',
    title: 'Driving Sales Through Integrated Marketing Campaign',
    client: 'Premium Real Estate Development',
    industry: 'real-estate-development',
    services: ['advertising-campaign-management', 'marketing-strategy-planning'],
    challenge: 'A luxury real estate developer launching a new mixed-use development needed to generate awareness and drive pre-sales in a competitive market. Traditional advertising alone was not reaching their target demographic of affluent buyers and investors.',
    strategy: 'Created an integrated marketing campaign combining digital advertising, traditional media, experiential events, and strategic partnerships. Developed targeted messaging for different buyer segments and implemented a multi-touch attribution model to optimize spend.',
    execution: 'Launched coordinated campaign across digital channels (social media, search, display), print advertising in premium publications, exclusive preview events for brokers and qualified buyers, and strategic PR placements. Implemented sophisticated tracking to measure campaign effectiveness across all touchpoints.',
    outcomes: [
      { metric: 'Pre-Sales Generated', result: '78% sold', timeframe: '9 months' },
      { metric: 'Campaign ROI', result: '420%', timeframe: '9 months' },
      { metric: 'Qualified Buyer Inquiries', result: '+340%', timeframe: '6 months' },
      { metric: 'Brand Awareness', result: '+85%', timeframe: '9 months' }
    ],
    testimonial: {
      quote: 'The integrated approach San Gabriel brought to our launch exceeded all expectations. They understood our market and delivered results that directly impacted our bottom line.',
      author: 'VP of Marketing',
      company: 'Premium Real Estate Development'
    },
    featured: true
  },
  {
    id: 'healthcare-patient-acquisition',
    title: 'Scaling Patient Acquisition for Healthcare Network',
    client: 'Regional Healthcare Network',
    industry: 'healthcare-wellness',
    services: ['digital-presence-growth', 'advertising-campaign-management'],
    challenge: 'A growing healthcare network needed to increase patient volume across multiple locations while maintaining compliance with healthcare marketing regulations. Their existing marketing efforts were fragmented and lacked consistent messaging or measurement.',
    strategy: 'Developed compliant digital marketing strategy focused on local SEO, patient education content, and targeted advertising for key service lines. Created unified brand messaging while allowing for location-specific customization.',
    execution: 'Optimized Google Business profiles for all locations, launched educational content program addressing common patient concerns, implemented compliant paid search and social campaigns, developed patient referral program, and established comprehensive analytics dashboard for tracking patient acquisition by source and location.',
    outcomes: [
      { metric: 'New Patient Appointments', result: '+165%', timeframe: '12 months' },
      { metric: 'Organic Search Traffic', result: '+220%', timeframe: '12 months' },
      { metric: 'Cost Per Acquisition', result: '-35%', timeframe: '12 months' },
      { metric: 'Patient Satisfaction Scores', result: '+18%', timeframe: '12 months' }
    ],
    testimonial: {
      quote: 'San Gabriel understood the unique challenges of healthcare marketing. Their strategic approach helped us grow while maintaining the trust and compliance that are essential in our industry.',
      author: 'Chief Marketing Officer',
      company: 'Regional Healthcare Network'
    },
    featured: false
  },
  {
    id: 'retail-omnichannel-growth',
    title: 'Omnichannel Strategy Drives Retail Growth',
    client: 'Specialty Retail Chain',
    industry: 'retail-consumer-brands',
    services: ['marketing-strategy-planning', 'digital-presence-growth', 'advertising-campaign-management'],
    challenge: 'A specialty retail chain with 12 locations faced declining foot traffic and struggled to compete with online retailers. They needed to create a seamless omnichannel experience and drive both in-store and online sales.',
    strategy: 'Developed comprehensive omnichannel strategy integrating in-store experience with digital touchpoints. Created unified customer journey across all channels with consistent messaging and personalized engagement based on customer behavior and preferences.',
    execution: 'Rebuilt e-commerce platform with improved user experience, implemented buy-online-pickup-in-store functionality, launched loyalty program connecting online and offline purchases, created location-based mobile advertising campaigns, and developed content strategy showcasing products and in-store experiences.',
    outcomes: [
      { metric: 'Total Revenue', result: '+92%', timeframe: '18 months' },
      { metric: 'Online Sales', result: '+280%', timeframe: '18 months' },
      { metric: 'In-Store Traffic', result: '+45%', timeframe: '18 months' },
      { metric: 'Customer Lifetime Value', result: '+67%', timeframe: '18 months' }
    ],
    testimonial: {
      quote: 'San Gabriel helped us bridge the gap between our physical stores and digital presence. The omnichannel strategy they developed has been transformational for our business.',
      author: 'CEO',
      company: 'Specialty Retail Chain'
    },
    featured: false
  },
  {
    id: 'tech-startup-market-entry',
    title: 'Successful Market Entry for B2B SaaS Startup',
    client: 'B2B SaaS Technology Company',
    industry: 'technology-startups',
    services: ['marketing-strategy-planning', 'brand-development-positioning', 'digital-presence-growth'],
    challenge: 'A B2B SaaS startup with innovative technology struggled to gain market traction. They had a strong product but lacked brand recognition, clear positioning, and an effective go-to-market strategy to reach their target enterprise customers.',
    strategy: 'Developed comprehensive go-to-market strategy including brand positioning, messaging framework, and multi-channel demand generation program. Focused on establishing thought leadership and building credibility in a competitive market.',
    execution: 'Created brand identity and positioning that differentiated the company from competitors, developed content marketing program targeting key decision-makers, launched targeted LinkedIn and industry publication campaigns, implemented marketing automation for lead nurturing, and established strategic partnerships for co-marketing opportunities.',
    outcomes: [
      { metric: 'Qualified Leads', result: '+425%', timeframe: '12 months' },
      { metric: 'Enterprise Customers', result: '23 new', timeframe: '12 months' },
      { metric: 'Brand Awareness', result: '+310%', timeframe: '12 months' },
      { metric: 'Marketing ROI', result: '380%', timeframe: '12 months' }
    ],
    testimonial: {
      quote: 'San Gabriel gave us the strategic foundation we needed to scale. Their understanding of B2B marketing and ability to execute across multiple channels was exactly what we needed.',
      author: 'Co-Founder & CEO',
      company: 'B2B SaaS Technology Company'
    },
    featured: true
  }
];

export default caseStudies;
